import React, { useState, useEffect, FunctionComponent } from 'react';
import { MedisanteDevice, DeviceModels } from '../../../../pages/Device/types';
import {
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Slider,
  DialogActions,
  Button,
  Theme,
} from '@material-ui/core';
import FilterIcon from '@material-ui/icons/LineWeight';
import { makeStyles } from '@material-ui/styles';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useChangeMinWeightMutation } from '../../../../store/api-slice';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    marginRight: '8px',
  },
  slider: {
    padding: '70px 40px 20px 40px',
    borderRadius: '4px',
    backgroundColor: theme.palette.background.default,
  },
  actions: {
    padding: theme.spacing(0, 1, 1, 1),
  },
  title: {
    marginTop: theme.spacing(1),
  },
}));

interface Props {
  shouldShowItem: boolean;
  onEdit: () => void;
  disabled: boolean;
}

interface DialogProps {
  open: boolean;
  devices: MedisanteDevice[];
  onClose: () => void;
  onSave: () => void;
}

const FilterMenuItem = (props: Props) => {
  const { shouldShowItem, onEdit, disabled } = props;
  const css = useStyles();

  if (shouldShowItem) {
    return (
      <MenuItem onClick={onEdit} disabled={disabled}>
        <FilterIcon className={css.icon} /> Minimum weight filter
      </MenuItem>
    );
  }

  return null;
};

const unit = 'kg';
const step = 1;
const dLimit = 15;
const mLimit = 40;

const marks = [
  { value: 0, label: `Off` },
  { value: dLimit, label: `${dLimit}${unit}` },
  { value: mLimit, label: `${mLimit}${unit}` },
];

const FilterDialog: FunctionComponent<DialogProps> = ({
  open = false,
  onClose = () => {},
  devices = [],
  onSave = () => {},
}) => {
  const css = useStyles();
  const { authUser } = useAuthUser();
  const { apiBaseURL } = useConfig();
  const [minValue, setMinValue] = useState(0);
  const [limit, setLimit] = useState(dLimit);
  const { enqueueSnackbar } = useSnackbar();

  const [changeMinWeight, { isLoading }] = useChangeMinWeightMutation();

  useEffect(() => {
    const max = devices.reduce((val, item) => {
      if (item.minimumWeight && val < item.minimumWeight) {
        return item.minimumWeight;
      }

      return val;
    }, 0);

    setLimit(max);

    const mValue = devices.reduce((val, item) => {
      // Minimum weight option 15kg and above for 2.4 scales and BT005
      if (
        item.version === '2.4.0' ||
        item.model === DeviceModels.BT005 ||
        item.model === DeviceModels.BS2001G ||
        item.model === DeviceModels.PS300CRPM
      ) {
        return 15;
      }
      return 0;
    }, 0);

    setMinValue(mValue);
  }, [devices]);

  const handleValueChanged = (event: any, value: number | number[]) => {
    if (Array.isArray(value)) {
      setLimit(value[0]);
    } else {
      setLimit(value);
    }
  };

  const handleSave = () => {
    const value = limit < minValue ? minValue : limit;
    changeMinWeight({
      apiBaseURL,
      devices,
      orgIDs: authUser.selectedOrgs,
      minWeight: value,
    }).then((resp: any) => {
      if (!resp.error) {
        onSave();
        return;
      }
      enqueueSnackbar(parseErrorMessage(resp.error), { variant: 'error' });
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle className={css.title}>Minimum weight filter</DialogTitle>
      <DialogContent>
        <div className={css.slider}>
          <Slider
            value={limit}
            onChange={handleValueChanged}
            step={step}
            min={minValue}
            max={mLimit}
            marks={marks}
            valueLabelDisplay="auto"
          />
        </div>
      </DialogContent>
      <DialogActions className={css.actions}>
        <Button color="primary" onClick={onClose}>
          Cancel
        </Button>
        <ButtonProgress
          color="primary"
          isLoading={isLoading}
          variant="contained"
          onClick={handleSave}
        >
          Save
        </ButtonProgress>
      </DialogActions>
    </Dialog>
  );
};

export { FilterMenuItem, FilterDialog };
